/**
 * us-states — the one code → name table for U.S. states, the District of
 * Columbia, and the inhabited territories, plus the helpers every surface
 * uses to normalize a customer-supplied state.
 *
 * The state-legislature replica scope is the 50 states + DC only; use
 * `STATE_AND_DC_CODES` for that contract and `US_STATE_NAMES` when a
 * territory code must also resolve (federal member lookups, the demo API).
 */

export const US_STATE_NAMES: Readonly<Record<string, string>> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado",
  CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho",
  IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma",
  OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming", DC: "District of Columbia",
  AS: "American Samoa", GU: "Guam", MP: "Northern Mariana Islands", PR: "Puerto Rico", VI: "Virgin Islands",
};

export const US_TERRITORY_CODES: readonly string[] = ["AS", "GU", "MP", "PR", "VI"];

/** The 50 states plus DC, in table order — the exact state-legislature replica scope. */
export const STATE_AND_DC_CODES: readonly string[] = Object.keys(US_STATE_NAMES)
  .filter((code) => !US_TERRITORY_CODES.includes(code));

const CODE_BY_NAME = new Map(
  Object.entries(US_STATE_NAMES).map(([code, name]) => [name.toLowerCase(), code]),
);

/** OCD division or jurisdiction id, e.g. ocd-jurisdiction/country:us/state:pa/government. */
const OCD_US_STATE = /^ocd-(?:division|jurisdiction)\/country:us\/(?:state|district|territory):([a-z]{2})(?:\/.*)?$/i;

/**
 * Accepts "TX", "tx", "us-tx", "Texas", or an OCD division/jurisdiction id
 * and returns the two-letter code, or null when the input names no known
 * state, DC, or territory.
 */
export function stateCodeFromInput(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const ocd = OCD_US_STATE.exec(trimmed);
  if (ocd) {
    const code = ocd[1]!.toUpperCase();
    return US_STATE_NAMES[code] ? code : null;
  }
  const upper = trimmed.toUpperCase().replace(/^US-/, "");
  if (US_STATE_NAMES[upper]) return upper;
  return CODE_BY_NAME.get(trimmed.toLowerCase()) ?? null;
}

/** Display name for a two-letter code, or null when unknown. */
export function stateNameFromCode(code: string | null | undefined): string | null {
  if (!code) return null;
  return US_STATE_NAMES[code.trim().toUpperCase()] ?? null;
}

export type UsCensusRegion = "Northeast" | "Midwest" | "South" | "West";

/** U.S. Census Bureau regions for the 50 states plus DC (browse/filter grouping only). */
export const US_CENSUS_REGIONS: Readonly<Record<string, UsCensusRegion>> = {
  CT: "Northeast", ME: "Northeast", MA: "Northeast", NH: "Northeast", RI: "Northeast", VT: "Northeast",
  NJ: "Northeast", NY: "Northeast", PA: "Northeast",
  IL: "Midwest", IN: "Midwest", MI: "Midwest", OH: "Midwest", WI: "Midwest", IA: "Midwest", KS: "Midwest",
  MN: "Midwest", MO: "Midwest", NE: "Midwest", ND: "Midwest", SD: "Midwest",
  DE: "South", DC: "South", FL: "South", GA: "South", MD: "South", NC: "South", SC: "South", VA: "South",
  WV: "South", AL: "South", KY: "South", MS: "South", TN: "South", AR: "South", LA: "South", OK: "South", TX: "South",
  AZ: "West", CO: "West", ID: "West", MT: "West", NV: "West", NM: "West", UT: "West", WY: "West",
  AK: "West", CA: "West", HI: "West", OR: "West", WA: "West",
};
